import {Component, computed, effect, inject, input, signal} from '@angular/core';
import {MapComponent} from '../../../../../../../shared/components/map/map.component';
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card';
import {MatIconButton} from '@angular/material/button';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';
import {BaseStateReportWidget} from '../base-state-report-widget';
import { MetricLayerFeature, RestorationService } from '../../../../../../../services/restoration.service';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {Feature} from 'ol';
import {Geometry} from 'ol/geom';
import {MetricDetails} from '../../../../../../../shared/classes/metric-details';
import {rxResource} from '@angular/core/rxjs-interop';
import {EMPTY} from 'rxjs';
import {MapService} from '../../../../../../../services/map.service';
import GeoJSON from 'ol/format/GeoJSON';
import { scaleLinear } from 'd3-scale';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {CompactNumberPipe} from '../../../../../../../shared/pipes/compact-number.pipe';
import { PopoverData } from '../../../../../../../shared/classes/popover-data';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'tgrid-restoration-metric-map-widget',
  imports: [
    MapComponent,
    MatCard,
    MatCardContent,
    MatCardTitle,
    MatIconButton,
    VulcanIconComponent,
    CompactNumberPipe,
  ],
  templateUrl: './restoration-metric-map-widget.component.html',
  styleUrl: './restoration-metric-map-widget.component.scss',
})
export class RestorationMetricMapWidgetComponent extends BaseStateReportWidget {
  private numberPipe = new DecimalPipe('en-US');
  title = input.required<string>();
  subtitle = input.required<string>();
  metric = input.required<'impact_level' | 'auc' | 'recovery_duration'>();
  countyLabelPrefix = input<string>('Most impacted counties:');
  exportFileName = input.required<string>();
  includeNtlData = input<boolean>(true);

  readonly mapId = input.required<string>();

  private restorationService = inject(RestorationService);
  private countyLayer: VectorLayer<VectorSource<Feature<Geometry>>> | null = null;
  protected legendMin = signal<number>(0);
  protected legendMax = signal<number>(0);
  protected topCounties = signal<MetricDetails[]>([]);
  protected topCountyNames = computed(() =>
    this.topCounties().map(c => c.county).join(', '),
  );

  protected mapData = rxResource({
    request: () => ({
      state: this.territories().filter(t => t.abbreviation === this.stateParam()),
      year: Number(this.yearParam()),
    }),
    loader: ({ request }) => {
      if (!request.state.length || !request.year) return EMPTY;
      return this.restorationService.getMetricLayer(this.metric(), 'max', request.state, [request.year], this.includeNtlData());
    },
  });

  constructor() {
    super();

    effect(() => {
      const data = this.mapData.value();
      if (!data) return;

      const min = data.scale.min;
      const max = data.scale.max;

      const stateAbbr = this.stateParam();

      this.legendMin.set(min);
      this.legendMax.set(max);

      const filteredFeatures = data.features.filter(
        (feature) => feature.properties.stateAbbreviation === stateAbbr,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ).map((feature: any) => {
        feature.properties.popoverData = this.createPopover(feature);
        return feature satisfies MetricLayerFeature;
      });

      const sorted = [...filteredFeatures]
        .map((feature) => this.getFeatureMetric(feature))
        .filter((metric): metric is MetricDetails => metric?.max !== undefined)
        .sort((a, b) => b.max - a.max)
        .slice(0, 5);

      this.topCounties.set(sorted);
      this.addLayerToMap(filteredFeatures, { min, max, toValue: (value: number) => value });
    });
  }

  private getFeatureMetric(feature: MetricLayerFeature): MetricDetails | undefined {
    return feature.properties.tastiDetails;
  }

  private addLayerToMap(features: MetricLayerFeature[], scale: { min: number, max: number, toValue: (value: number) => number }): void {
    const map = MapService.mapRefs[this.mapId()];
    if (!map) return;

    if (this.countyLayer) {
      map.removeLayer(this.countyLayer);
    }

    const geoJsonFeatures = new GeoJSON().readFeatures(
      { type: 'FeatureCollection', features },
    );

    const colorScale = scaleLinear([scale.min, scale.max], ['#e8cc65', '#c14a22']);

    const hatchPattern = (() => {
      const canvas = document.createElement('canvas');
      canvas.width = 12;
      canvas.height = 12;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return undefined;
      }

      // Use thicker, higher-contrast strokes and a larger tile for a clearer NTL hatch.
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, 12);
      ctx.lineTo(12, 0);
      ctx.stroke();
      return ctx.createPattern(canvas, 'repeat') ?? undefined;
    })();

    this.countyLayer = new VectorLayer<VectorSource<Feature<Geometry>>>({
      source: new VectorSource<Feature<Geometry>>({
        features: geoJsonFeatures as unknown as Feature<Geometry>[],
      }),
      opacity: 0.8,
      style: (feature) => {
        const properties = feature.getProperties() as { tastiDetails?: MetricDetails, ntlDetails?: MetricDetails };
        const legendValue = properties.tastiDetails?.max !== undefined ? scale.toValue(properties.tastiDetails.max) : undefined;
        const hasNTL = properties.ntlDetails?.max !== undefined;
        const color = legendValue === undefined ? '#444' : colorScale(legendValue);

        const baseStyle = new Style({
          stroke: new Stroke({
            color: 'black',
            width: 2,
          }),
          fill: new Fill({
            color,
          }),
        });

        if (hasNTL) {
          return [
            baseStyle,
            new Style({
              fill: new Fill({ color: hatchPattern }),
            }),
          ];
        }

        return baseStyle;
      },
    });

    map.addLayer(this.countyLayer);

    const source = this.countyLayer.getSource();
    if (source) {
      map.getView().fit(source.getExtent(), { padding: [20, 20, 20, 20] });
    }
  }

  protected override async export(): Promise<void> {
    try {
      const mapUrl = await html2canvas(this.exportContent()?.nativeElement, {
        ignoreElements: (element) => element.classList.contains('legend'),
      }).then(canvas => canvas.toDataURL('image/png'));
      const legendUrl = await html2canvas(this.legendContent()?.nativeElement).then(canvas => canvas.toDataURL('image/png'));

      const zip = new JSZip();
      const baseName = `${this.yearParam()}_${this.stateParam()}_${this.exportFileName()}`;
      zip.file(`${baseName}_map.png`, mapUrl.split(',')[1], { base64: true });
      zip.file(`${baseName}_legend.png`, legendUrl.split(',')[1], { base64: true });

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(zipBlob);
      anchor.download = `${baseName}.zip`;
      anchor.click();
      this.snackBar.open('Export downloaded successfully', 'Close', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackBar.open('Export failed. Please try again.', 'Close', { duration: 3000 });
    }
  }

  private createPopover(feature: MetricLayerFeature): PopoverData[] {
    const popoverData: PopoverData[] = [];
    const formatNumber = (x: number): string => {
      const formatted = this.numberPipe.transform(x, '1.2-2');
      return formatted ? '$' + formatted : 'Not Available';
    };
    popoverData.push(new PopoverData({ displayText: 'State', displayValue: feature.properties.stateAbbreviation }));
    popoverData.push(new PopoverData({ displayText: 'County', displayValue: feature.properties.county }));
    popoverData.push(new PopoverData({ displayText: '\u00A0', displayValue: '\u00A0' })); // spacer
    const tasti = feature.properties.tastiDetails;
    const ntl = feature.properties.ntlDetails;
    if (tasti) {
      popoverData.push(new PopoverData({ displayText: 'Source', displayValue: 'TASTI-GRID' }));
      popoverData.push(new PopoverData({ displayText: 'Min', displayValue: formatNumber(tasti.min) }));
      popoverData.push(new PopoverData({ displayText: 'Max', displayValue: formatNumber(tasti.max) }));
      popoverData.push(new PopoverData({ displayText: 'Average', displayValue: formatNumber(tasti.avg) }));
      popoverData.push(new PopoverData({ displayText: 'Sum', displayValue: formatNumber(tasti.sum) }));
    }
    if (tasti && ntl) {
      popoverData.push(new PopoverData({ displayText: '\u00A0', displayValue: '\u00A0' })); // spacer
    }
    if (ntl) {
      popoverData.push(new PopoverData({ displayText: 'Source', displayValue: 'Night Time Lights' }));
      popoverData.push(new PopoverData({ displayText: 'Min', displayValue: formatNumber(ntl.min) }));
      popoverData.push(new PopoverData({ displayText: 'Max', displayValue: formatNumber(ntl.max) }));
      popoverData.push(new PopoverData({ displayText: 'Average', displayValue: formatNumber(ntl.avg) }));
      popoverData.push(new PopoverData({ displayText: 'Sum', displayValue: formatNumber(ntl.sum) }));
    }
    return popoverData;
  }
}
